import { useState } from 'react'
import { useMeetingNotes } from './useMeetingNotes'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-notes')

interface MeetingNotesProps {
  roomId: string | undefined
  isHost: boolean
  isAdmin: boolean
  onClose: () => void
}

export function MeetingNotes({ roomId, isHost, isAdmin, onClose }: MeetingNotesProps) {
  const { notes, updateNotes, summary, generating, saving, saveNotes, generateSummary } = useMeetingNotes({ roomId, enabled: true })
  const [copied, setCopied] = useState(false)
  const canGenerate = isHost || isAdmin

  const copyToClipboard = () => {
    const content = summary || notes
    if (!content) return
    navigator.clipboard.writeText(content).then(() => {
      slog.action('notes-copy', { roomId })
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  const sendByEmail = () => {
    slog.action('notes-email', { roomId })
    const subject = encodeURIComponent('Notes de reunion')
    const body = encodeURIComponent(summary || notes || '')
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
  }

  return (
    <div className="absolute top-0 right-0 bottom-12 w-80 bg-zinc-900 border-l border-zinc-700 z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <h3 className="text-sm font-medium text-zinc-200">Notes de reunion</h3>
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-zinc-200 text-xs px-2 py-0.5 rounded hover:bg-zinc-700"
        >
          Fermer
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* My notes */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wide">Mes notes</label>
            {saving && <span className="text-[9px] text-zinc-500">Sauvegarde...</span>}
          </div>
          <textarea
            value={notes}
            onChange={e => updateNotes(e.target.value)}
            onBlur={saveNotes}
            placeholder="Tapez vos notes ici..."
            className="w-full h-32 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-200 p-2 resize-y focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
          />
        </div>

        {/* AI Summary */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Resume AI</span>
            {canGenerate && (
              <button
                onClick={generateSummary}
                disabled={generating}
                className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                  generating ? 'bg-zinc-700 text-zinc-500' : 'bg-blue-600 text-white hover:bg-blue-500'
                }`}
              >
                {generating ? 'Generation...' : 'Generer'}
              </button>
            )}
          </div>
          {summary ? (
            <div className="bg-zinc-800 border border-zinc-700 rounded p-2 text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
              {summary}
            </div>
          ) : (
            <div className="bg-zinc-800 border border-zinc-700 rounded p-2 text-[10px] text-zinc-500 italic">
              {canGenerate ? 'Cliquez sur "Generer" pour creer un resume AI de la reunion.' : 'Aucun resume disponible.'}
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-3 py-2 border-t border-zinc-700 flex gap-2">
        <button
          onClick={copyToClipboard}
          disabled={!summary && !notes}
          className="flex-1 text-[10px] py-1.5 rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {copied ? 'Copie !' : 'Copier'}
        </button>
        <button
          onClick={sendByEmail}
          disabled={!summary && !notes}
          className="flex-1 text-[10px] py-1.5 rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Email
        </button>
      </div>
    </div>
  )
}
